import { Zap } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import SectionLabel from "@/components/site/SectionLabel";
import { PILLARS, TAGLINE } from "@/lib/site";

export default function Pillars() {
    return (
        <section
            data-testid="pillars-section"
            className="relative bg-[#020617] py-24 md:py-32 overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#1E90FF]/10 blur-[160px] pointer-events-none"
            />

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
                    <div className="md:col-span-6">
                        <Reveal>
                            <SectionLabel testId="pillars-label">
                                The 4 Pillars
                            </SectionLabel>
                            <h2 className="mt-6 font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
                                Built on{" "}
                                <span className="text-[#00B7FF] text-glow-blue">
                                    four
                                </span>{" "}
                                non-negotiables.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-6">
                        <Reveal delay={0.1}>
                            <p className="text-white/70 leading-relaxed text-base sm:text-lg">
                                Real attraction isn’t a tactic — it’s the
                                byproduct of four forces working in parallel.{" "}
                                {TAGLINE} trains all four at once, so you don’t
                                level up in one dimension while neglecting the
                                others.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {PILLARS.map((p, i) => (
                        <Reveal key={p.no} delay={i * 0.08}>
                            <article
                                data-testid={`pillar-card-${i + 1}`}
                                className="group relative bg-gradient-to-b from-[#0a1429] to-[#020617] border border-[#1a2a4a] hover:border-[#1E90FF]/60 transition-all duration-500 p-7 h-full overflow-hidden"
                            >
                                <div
                                    aria-hidden
                                    className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-[#1E90FF]/0 group-hover:bg-[#1E90FF]/20 blur-3xl transition-all duration-700"
                                />

                                <div className="relative flex items-start justify-between">
                                    <span className="font-display font-black text-[#1E90FF]/70 text-4xl leading-none group-hover:text-[#00B7FF] transition-colors">
                                        {p.no}
                                    </span>
                                    <Zap
                                        size={18}
                                        className="text-[#1E90FF]/40 group-hover:text-[#00B7FF] transition-colors"
                                        fill="currentColor"
                                    />
                                </div>

                                <h3 className="relative mt-10 font-display font-black text-white text-2xl tracking-tight uppercase">
                                    {p.title}
                                </h3>
                                {p.sub && (
                                    <p className="relative mt-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#00B7FF]">
                                        {p.sub}
                                    </p>
                                )}
                                <p className="relative mt-5 text-white/65 leading-relaxed text-sm">
                                    {p.desc}
                                </p>

                                <div className="relative mt-8 h-px w-10 bg-[#1E90FF] group-hover:w-20 transition-all duration-500" />
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
